// src/components/separation/uiHelpers.ts
// Shared presentation helpers for the Separation module so every list/card
// renders statuses, names, dates and money the same way.

export interface StatusMeta {
  label: string;
  cls: string;
}

// Covers resignation, settlement and checklist statuses in one map.
const STATUS_META: Record<string, StatusMeta> = {
  PENDING: { label: 'Pending', cls: 'bg-amber-100 text-amber-700' },
  MANAGER_APPROVED: { label: 'Manager Approved', cls: 'bg-blue-100 text-blue-700' },
  MANAGER_REJECTED: { label: 'Manager Rejected', cls: 'bg-red-100 text-red-700' },
  HR_APPROVED: { label: 'HR Approved', cls: 'bg-indigo-100 text-indigo-700' },
  HR_REJECTED: { label: 'HR Rejected', cls: 'bg-red-100 text-red-700' },
  ADMIN_APPROVED: { label: 'Admin Approved', cls: 'bg-emerald-100 text-emerald-700' },
  ADMIN_REJECTED: { label: 'Admin Rejected', cls: 'bg-red-100 text-red-700' },
  WITHDRAWN: { label: 'Withdrawn', cls: 'bg-gray-100 text-gray-600' },
  COMPLETED: { label: 'Completed', cls: 'bg-emerald-100 text-emerald-700' },
  // Settlement / checklist statuses
  CALCULATED: { label: 'Calculated', cls: 'bg-blue-100 text-blue-700' },
  APPROVED: { label: 'Approved', cls: 'bg-indigo-100 text-indigo-700' },
  PAID: { label: 'Paid', cls: 'bg-emerald-100 text-emerald-700' },
  IN_PROGRESS: { label: 'In Progress', cls: 'bg-amber-100 text-amber-700' },
};

export function statusBadge(status: string): StatusMeta {
  return STATUS_META[status] || { label: status || '—', cls: 'bg-gray-100 text-gray-600' };
}

export const TERMINATION_TYPE_LABEL: Record<string, string> = {
  GROSS_MISCONDUCT: 'Gross Misconduct',
  PERFORMANCE: 'Performance',
  REDUNDANCY: 'Redundancy',
  CONTRACT_END: 'Contract End',
  VOLUNTARY: 'Voluntary',
  OTHER: 'Other',
};

const TERMINATION_TYPE_CLS: Record<string, string> = {
  GROSS_MISCONDUCT: 'bg-red-100 text-red-700',
  PERFORMANCE: 'bg-amber-100 text-amber-700',
  REDUNDANCY: 'bg-orange-100 text-orange-700',
  CONTRACT_END: 'bg-blue-100 text-blue-700',
  VOLUNTARY: 'bg-emerald-100 text-emerald-700',
  OTHER: 'bg-gray-100 text-gray-600',
};

export function terminationTypeBadge(type: string): StatusMeta {
  return {
    label: TERMINATION_TYPE_LABEL[type] || type || '—',
    cls: TERMINATION_TYPE_CLS[type] || 'bg-gray-100 text-gray-600',
  };
}

// Prefer the real name attached by the backend; fall back to a short id so a
// row is never blank if the employee record is missing.
export function employeeLabel(row: { employee_name?: string; employee_id: string }): string {
  const name = (row.employee_name || '').trim();
  return name || `Employee ${(row.employee_id || '').slice(0, 8)}`;
}

export function fmtDate(d?: string | null): string {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function fmtMoney(n?: number | null): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(n || 0));
}
